import { TextureColorSpace, TextureFormat } from '@seddi/surface-material';
import { type LoadingManager, Matrix3, type WebGLRenderer } from '@seddi/three';
import { type GarmentOcclusion, PieceSide } from '@seddi/scene';

import type { GarmentOcclusionGfx } from './garmentOcclusionTypes';
import { load as loadTexture } from './textureLoader';

interface KHRTextureTransform {
  offset?: [number, number];
  rotation?: number;
  scale?: [number, number];
}

const SIDE_MAP: Record<string, PieceSide> = {
  bk: PieceSide.BACK,
  fr: PieceSide.FRONT,
};

// Convert KHR_texture_transform (glTF UV space: origin top-left, v downward) to a Matrix3
// in WebGL UV space (origin bottom-left, v upward). Derived by substituting v_gltf = 1 - v_gl,
// applying the glTF transform, then converting the result back.
function composeKhrTextureTransform(khr: KHRTextureTransform): Matrix3 {
  const tx = khr.offset?.[0] ?? 0;
  const ty = khr.offset?.[1] ?? 0;
  const r = khr.rotation ?? 0;
  const sx = khr.scale?.[0] ?? 1;
  const sy = khr.scale?.[1] ?? 1;

  const cosR = Math.cos(r);
  const sinR = Math.sin(r);

  return new Matrix3().set(
    cosR * sx,
    sinR * sy,
    tx - sinR * sy,
    -sinR * sx,
    cosR * sy,
    1 - ty - cosR * sy,
    0,
    0,
    1,
  );
}

/**
 * Load garment occlusion.
 * @param desc - Garment occlusion descriptor.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 */
export async function load(
  desc: GarmentOcclusion,
  manager: LoadingManager,
  renderer: WebGLRenderer,
): Promise<GarmentOcclusionGfx> {
  const { id } = desc;
  const [gltf, textureFr, textureBk] = await Promise.all([
    fetch(desc.gltf.href).then((r) => r.json()),
    loadTexture(
      {
        colorSpace: TextureColorSpace.LINEAR,
        format: TextureFormat.WEBP,
        href: desc.imageFr.href,
        id: `${id}:fr`,
      },
      manager,
      renderer,
    ),
    loadTexture(
      {
        colorSpace: TextureColorSpace.LINEAR,
        format: TextureFormat.WEBP,
        href: desc.imageBk.href,
        id: `${id}:bk`,
      },
      manager,
      renderer,
    ),
  ]);

  const textureInfos: GarmentOcclusionGfx['textureInfos'] = {};

  for (const material of gltf.materials ?? []) {
    const name: string = material.name ?? '';
    const colonIdx = name.lastIndexOf(':');
    if (colonIdx === -1) continue;

    const side = SIDE_MAP[name.slice(colonIdx + 1)];
    if (!side) continue;

    const occlusionTexture = material.occlusionTexture;
    if (!occlusionTexture) continue;

    const strength: number | undefined = occlusionTexture.strength;
    const transform = composeKhrTextureTransform(occlusionTexture.extensions.KHR_texture_transform);

    textureInfos[name] = { side, strength, transform };
  }

  return { id: desc.id, textureFr, textureBk, textureInfos };
}
